import React from 'react';
import { useTranslation } from 'next-i18next';
import Icon from '@components/atoms/icon/icon';
import BucketTestProductInstallment from '@components/bucketTest/bucket-test-product-installment';
import { useBucket } from '@context/bucket.context';
import TagContainer from '@enums/tagContainer';
import { useAnalytics } from '@context/analytics.context';
import { NegotiationProvider } from '@context/negotiation.context';
import useProductShippingQuery from '@hooks/product/use-product-shipping-query';
import useProductQuery from '@hooks/product/use-product-query';
import UserBadge from '@enums/userBadge';
import WelcomeVoucherWrapper from '@components/catalog/welcome-voucher/welcome-voucher-wrapper';
import dynamic from 'next/dynamic';
import SellerOnVacationWrapper from '@components/molecules/products/product-details/components/seller-on-vacation/seller-on-vacation-wrapper';
import ProductSellerBadge from '../product-seller-badge/product-seller-badge';
import AdditionalDetails from './components/additional-details/additional-details';
import CTAProduct from './components/CTA-product/CTA-product';
import ProductCharacteristics from './components/product-characteristics/product-characteristics';
import ProductPrice from './components/product-price/product-price';
import styles from './product-details.module.scss';

const ProductSellerActions = dynamic(() => import('./components/product-seller-actions/product-seller-actions'));

const ProductAuthInfo = dynamic(() => import('./components/product-authentication-infos/product-authentication-infos'));

const ShippingRestriction = dynamic(() => import('./components/shipping-restriction/shipping-restriction'));

const ProductDetails: React.FC = () => {
  const { t } = useTranslation();
  const { sendEvent } = useAnalytics();
  const { isFeatureEnabled } = useBucket();
  const { productShipping } = useProductShippingQuery();
  const { product, userTransactionRole, isProductDirectShippableForCurrentUser } = useProductQuery();
  const monetizationM1 = isFeatureEnabled((f) => !!f.monetizationM1);
  const useMonetization = product.pricingBreakdown;
  const hasMandatoryAuthentication =
    useMonetization && monetizationM1 && (product.inStock || !isProductDirectShippableForCurrentUser);

  const isSeller: boolean = userTransactionRole === 'seller';
  const isOfficialStore: boolean = product.seller.badges?.includes(UserBadge.OfficialStore);
  const IsConsignment: boolean =
    product.consignment && !(product.consignment && product.seller.internalAccount) && !isOfficialStore;

  const shouldShowShippingRestriction = productShipping?.prohibited && !isSeller;

  const trackConsignmentClick = (e): void => {
    sendEvent(
      {
        type: 'event',
        container: TagContainer.Analytics,
        payload: {
          event: 'concierge_service',
          position: 'user_details',
          env_template: 'product',
        },
      },
      {
        type: 'ClickConsignmentCTA - productPage',
        container: TagContainer.Media,
        payload: {
          eventCategory: 'product',
          eventAction: 'concierge_service',
          eventLabel: 'profil : product_product_details',
        } as never,
      },
    );
    e.stopPropagation();
  };

  // seller, price, ds, condition, klarna, discount, cta, actions
  return (
    <div className={styles.productDetails}>
      <div className={styles.productDetails__top}>
        <div className={styles.productDetails__resume}>
          {product.seller && (
            <div className={styles.productDetails__seller}>
              <div className={styles.productDetails__seller__badge}>
                <ProductSellerBadge seller={product.seller} />
              </div>
              <div className={styles.productDetails__seller__text}>
                <a
                  className={styles.productDetails__seller__text__link}
                  href={`/profile/${product?.seller?.id}`}
                  rel="noopener"
                >
                  {`${t('PRODUCT.DETAILS.SELLING_BY')} ${product?.seller.firstname}`}
                </a>

                {IsConsignment && (
                  <a
                    // TODO: Update this anchor to <Link> when "CONSIGNMENT.SERVICE.LINK" is updated to /journal/...
                    className={styles.productDetails__sellerText__link}
                    href={t('CONSIGNMENT.SERVICE.LINK')}
                    onClick={(e) => trackConsignmentClick(e)}
                    rel="noopener"
                  >
                    <u className={styles.productDetails__sellerText__consignment}>{t('CONSIGNMENT.SERVICE')}</u>
                  </a>
                )}
              </div>
            </div>
          )}
          {/* Seller actions */}
          {isSeller && <ProductSellerActions product={product} />}
          {product?.inStock && (
            <p className={styles.productDetails__resume__express}>
              <Icon
                name="express"
                className={styles.productDetails__resume__express__icon}
              />
              <span>{t('DELIVERY_OPTS.EXPRESS')}</span>
            </p>
          )}
          {/* Product price */}
          <div className={styles.productDetails__resume__productPrice}>
            <ProductPrice
              isSeller={isSeller}
              product={product}
              directShippingEligible={isProductDirectShippableForCurrentUser}
              hasMandatoryAuthentication={hasMandatoryAuthentication}
            />
          </div>
          {/* Product characteristics */}
          <div className={styles.productDetails__resume__characteristics}>
            <ProductCharacteristics product={product} />
          </div>
          {/* Product auth */}
          {!isProductDirectShippableForCurrentUser && (
            <div className={styles.productDetails__resume__auth}>
              <ProductAuthInfo hasMandatoryAuthentication={hasMandatoryAuthentication} />
            </div>
          )}
        </div>
      </div>

      {/* Pay-in-installment provider */}
      <div className={styles.productDetails__resume__installment}>
        <BucketTestProductInstallment product={product} />
      </div>

      <WelcomeVoucherWrapper customClass={styles.productDetails__welcomeVoucher} />
      {shouldShowShippingRestriction && <ShippingRestriction />}
      <SellerOnVacationWrapper />
      <NegotiationProvider>{productShipping && <CTAProduct />}</NegotiationProvider>

      <AdditionalDetails />
    </div>
  );
};

export default ProductDetails;
